import type { MemberDataFirestoreRecord } from "@albertine/types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useState } from "react";
import type { Agent } from "../../common/types/Agent";
import type { AiSummary } from "../../common/types/AiSummary";
import type { Conversation } from "../../common/types/Conversation";
import type {
    InternalNote,
    Message,
    MessageOrInternalNoteOrAiSummary,
} from "../../common/types/Message";
import type { Request } from "../../common/types/Request";
import AiSummaryDisplay from "./AiSummaryDisplay";
import ChatInternalNote from "./ChatInternalNote";
import ChatMessage from "./ChatMessage";
import EditMessage from "./EditMessage";

type ChatMessageBaseProps = {
    idSelector: string;
    message: MessageOrInternalNoteOrAiSummary;
    conversation: Conversation;
    currentAgent: Agent;
    currentMember: MemberDataFirestoreRecord;
    isUnread: boolean;
    agents: Agent[];
    notPartOfRequest: boolean;
    currentRequest: Request | undefined;
    isLatestAiSummary?: boolean;
};

function isInternalNote(
    message: MessageOrInternalNoteOrAiSummary,
): message is InternalNote {
    return (
        "isInternalNote" in message && Boolean((message as InternalNote).isInternalNote)
    );
}

function isMessage(message: MessageOrInternalNoteOrAiSummary): message is Message {
    return !isInternalNote(message) && !isAiSummary(message);
}

function isAiSummary(message: MessageOrInternalNoteOrAiSummary): message is AiSummary {
    return "summaryType" in message;
}

function ChatMessageOrNote(props: ChatMessageBaseProps) {
    const {
        idSelector,
        message,
        conversation,
        currentAgent,
        currentMember,
        agents,
        isUnread,
        notPartOfRequest,
        currentRequest,
        isLatestAiSummary = false,
    } = props;

    const editState = useState<boolean>(false);
    const [isCurrentlyEdited, setIsCurrentlyEdited] = editState;

    // Check if AI summary features are enabled for this user
    const aiSummaryButtonEnabled =
        useFeatureFlagEnabled("ai_summary_button_enabled") ?? false;

    const messageId = message.id;

    // Only create editor for editable items (messages and notes, not AI summaries)
    const createMessageEditor = (editableMessage: Message | InternalNote) =>
        isCurrentlyEdited ? (
            <EditMessage
                conversation={conversation}
                currentAgent={currentAgent}
                member={currentMember}
                request={undefined}
                message={editableMessage}
                close={(action?: "cancel") => {
                    if (
                        !action ||
                        window.confirm(
                            "Are you sure you want to discard editing the message?",
                        )
                    ) {
                        setIsCurrentlyEdited(false);
                    }
                }}
            />
        ) : null;

    if (isInternalNote(message)) {
        return (
            <>
                <ChatInternalNote
                    {...{
                        idSelector,
                        internalNote: message,
                        conversation,
                        currentAgent,
                        currentMember,
                        agents,
                        editState,
                        notPartOfRequest,
                        currentRequest,
                    }}
                />
                {createMessageEditor(message)}
            </>
        );
    }

    if (isMessage(message)) {
        return (
            <>
                <ChatMessage
                    {...{
                        idSelector,
                        message,
                        conversation,
                        currentMember,
                        currentAgent,
                        editState,
                        isUnread,
                        notPartOfRequest,
                        currentRequest,
                    }}
                />
                {createMessageEditor(message)}
            </>
        );
    }

    if (isAiSummary(message)) {
        // Only show AI summaries to users who have the feature flag enabled
        if (!aiSummaryButtonEnabled) {
            return null;
        }

        return (
            <AiSummaryDisplay
                aiSummary={message}
                agents={agents}
                isLatest={isLatestAiSummary}
            />
        );
    }

    return (
        <div style={{ color: "red" }}>
            There is a problem showing this message with id: {messageId}. Please contact
            product team.
        </div>
    );
}

export default ChatMessageOrNote;
